<template>
  <div class="invoice-dense-tab">
    <app-collapse class="mb-1 invoice-dense-filters">
      <app-collapse-item title="Filters">
        <template #header>
          <span class="invoice-dense-filters__title">
            <feather-icon icon="FilterIcon" size="15" />
            <span>Filters</span>
          </span>
        </template>
        <div class="p-2">
          <b-row>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Start Date">
                <b-form-input v-model="filters.startDate" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="End Date">
                <b-form-input v-model="filters.endDate" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Client">
                <b-form-select v-model="filters.client" :options="clientSelectOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3" class="d-flex align-items-end">
              <b-button variant="primary" size="sm" @click="$emit('apply', { ...filters })">Apply</b-button>
            </b-col>
          </b-row>
        </div>
      </app-collapse-item>
    </app-collapse>

    <div class="invoice-dense-toolbar">
      <div class="invoice-dense-toolbar__search">
        <b-input-group size="sm">
          <template #prepend>
            <b-input-group-text class="invoice-dense-search-icon">
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-text>
          </template>
          <b-form-input v-model="search" placeholder="Search invoices…" :debounce="200" />
          <template v-if="search" #append>
            <b-button size="sm" variant="outline-secondary" @click="search = ''">
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </template>
        </b-input-group>
      </div>
      <div class="invoice-dense-toolbar__meta">
        <span v-if="!loading">{{ filteredItems.length }} invoice{{ filteredItems.length === 1 ? '' : 's' }}</span>
        <span v-else>Loading…</span>
      </div>
    </div>

    <div class="invoice-dense-table-wrap">
      <b-table
        :items="filteredItems"
        :fields="fields"
        hover
        responsive
        small
        :busy="loading"
        show-empty
        class="invoice-dense-table mb-0"
      >
        <template #table-busy>
          <div class="invoice-dense-state invoice-dense-state--loading">
            <b-spinner small class="mr-50" />
            <span>Loading finance insights…</span>
          </div>
        </template>
        <template #empty>
          <div class="invoice-dense-state">
            <div class="invoice-dense-empty-icon">
              <feather-icon icon="BarChart2Icon" size="28" />
            </div>
            <h6 class="mb-25">No finance insights</h6>
            <p class="text-muted mb-1">
              {{ search ? 'Try a different search or clear filters.' : 'Apply filters to load finance invoice rows.' }}
            </p>
            <b-button v-if="search" size="sm" variant="outline-secondary" @click="search = ''">Clear search</b-button>
          </div>
        </template>
        <template #cell(invoice_info)="{ item }">
          <div class="inv-cell-stack">
            <span class="inv-primary-text">{{ item.invoice_id || 'N/A' }}</span>
            <small class="text-muted">{{ formatDate(item.invoice_date) }}</small>
          </div>
        </template>
        <template #cell(client_gstin)="{ item }">
          <div class="inv-cell-stack">
            <span class="inv-primary-text">{{ item.client || 'N/A' }}</span>
            <small v-if="item.client_gst" class="inv-secondary-text">{{ item.client_gst }}</small>
          </div>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="getStatusVariant(item.status)" class="inv-status-badge">{{ item.status }}</b-badge>
        </template>
        <template #cell(total_amount)="{ item }"><span class="inv-amount">₹{{ formatIndianNumber(item.total_amount || 0) }}</span></template>
        <template #cell(paid_amount)="{ item }"><span class="inv-amount">₹{{ formatIndianNumber(item.paid_amount || 0) }}</span></template>
        <template #cell(outstanding)="{ item }"><span class="inv-amount">₹{{ formatIndianNumber(item.outstanding || 0) }}</span></template>
        <template #cell(due_date)="{ item }">{{ formatDate(item.due_date) }}</template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupText, BTable, BBadge, BButton, BSpinner } from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import invoiceFormatMixin from '@/mixins/invoiceFormatMixin'

export default {
  name: 'FinanceDashboardTab',
  components: { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupText, BTable, BBadge, BButton, BSpinner, AppCollapse, AppCollapseItem },
  mixins: [invoiceFormatMixin],
  props: {
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    loading: Boolean,
    clients: { type: Array, default: () => [] },
    initialFilters: {
      type: Object,
      default: () => ({ startDate: null, endDate: null, client: null })
    }
  },
  data() {
    return {
      search: '',
      filters: {
        startDate: this.initialFilters.startDate || null,
        endDate: this.initialFilters.endDate || null,
        client: this.initialFilters.client || null
      }
    }
  },
  computed: {
    clientSelectOptions() {
      return [
        { value: null, text: 'Select Client' },
        ...(this.clients || []).map(c => ({
          value: c.title != null ? c.title : (c.text != null ? c.text : c.value),
          text: c.text || c.title || String(c.value ?? '')
        }))
      ]
    },
    filteredItems() {
      let data = [...this.items]
      if (this.search) {
        const q = this.search.toLowerCase()
        data = data.filter(i => (i.invoice_id || '').toLowerCase().includes(q) || (i.client || '').toLowerCase().includes(q))
      }
      return data
    }
  }
}
</script>

<style lang="scss">
@import './invoice-dense-tab.scss';
</style>
